import {
  MAX_UPLOAD_BYTES,
  UPLOAD_IMAGE_TYPES,
  type ProductMedia,
  type ReplaceProductMediaRequest,
} from '@avrahami/contracts';
import { SVG_TYPE } from '@avrahami/ui';
import { tabStore } from './store-path';

/**
 * A plain link (an image, a document) sends no header, so it names this tab's store in the
 * address, and the API reads that store's session cookie (adr/0033).
 */
export function withStore(path: string, store: string | undefined = tabStore): string {
  return store === undefined ? path : `${path}?store=${encodeURIComponent(store)}`;
}

/** Where the admin previews an image of this store (catalog copy or upload). */
export function mediaUrl(file: string): string {
  return withStore(`/v1/tenant/media/${encodeURIComponent(file)}`);
}

/** Where the admin previews the image of a catalog product the store can still add. */
export function catalogSyncMediaUrl(file: string): string {
  return withStore(`/v1/tenant/catalog-sync/media/${encodeURIComponent(file)}`);
}

export type UploadProblem = 'TOO_LARGE' | 'NOT_AN_IMAGE';

/**
 * A quick check before uploading. The API decides the type from the bytes; this only saves a
 * round trip for files that are obviously wrong. An SVG passes: it is uploaded as a PNG.
 */
export function uploadProblem(file: { size: number; type: string }): UploadProblem | null {
  if (file.size > MAX_UPLOAD_BYTES) return 'TOO_LARGE';
  if (
    file.type !== '' &&
    file.type !== SVG_TYPE &&
    !(UPLOAD_IMAGE_TYPES as readonly string[]).includes(file.type)
  ) {
    return 'NOT_AN_IMAGE';
  }
  return null;
}

/** One image of a product's gallery while it is being edited. */
export type GalleryItem =
  | { key: string; kind: 'EXISTING'; id: string; file: string; altText: string }
  | { key: string; kind: 'UPLOAD'; mediaId: string; file: string; altText: string };

export function galleryItems(media: readonly ProductMedia[]): GalleryItem[] {
  return [...media]
    .sort((first, second) => first.position - second.position)
    .map((image) => ({
      key: image.id,
      kind: 'EXISTING',
      id: image.id,
      file: image.file,
      altText: image.altText ?? '',
    }));
}

/** The gallery as the API replaces it: existing images by id, uploads by media id, in order. */
export function galleryRequest(
  items: readonly GalleryItem[],
  version: number,
): ReplaceProductMediaRequest {
  return {
    version,
    items: items.map((item) => {
      const altText = item.altText.trim() === '' ? null : item.altText.trim();
      return item.kind === 'EXISTING'
        ? { kind: 'EXISTING', id: item.id, altText }
        : { kind: 'UPLOAD', mediaId: item.mediaId, altText };
    }),
  };
}

/** The quote request as a PDF (same origin: the session cookie goes with a plain link). */
export function orderDocumentUrl(orderId: string): string {
  return withStore(`/v1/tenant/orders/${encodeURIComponent(orderId)}/document`);
}
